'use client'

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useShell } from '../components/PageShell'
import ICCounterCard from '../components/admin/ICCounterCard'
import SystemHealthCard from '../components/admin/SystemHealthCard'
import AICoreCard from '../components/admin/AICoreCard'
import UsersCard from '../components/admin/UsersCard'
import AIPromptsCard from '../components/admin/AIPromptsCard'
import {
  Settings,
  Users,
  Database,
  Server,
  Shield,
  Activity,
  AlertTriangle,
  CheckCircle,
  Tag,
  MapPin,
  Package,
  BookOpen,
  List,
  FileCode,
  Bot,
  Wrench,
  Zap,
  Eye,
  ChevronRight,
  TrendingUp,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  GripVertical,
  EyeOff,
  Tag as LabelIcon,
  RotateCcw
} from 'lucide-react'

interface ManagementArea {
  id: string
  name: string
  description: string
  icon: React.ComponentType<any>
  href: string
  stats: {
    total: number
    active: number
    lastUpdated: string
  }
  status: 'online' | 'warning' | 'critical'
  glowColor: string
  visible?: boolean
  order?: number
}

const managementAreas: ManagementArea[] = [
  {
    id: 'categories',
    name: 'Categories',
    description: 'Product category hierarchy management',
    icon: Package,
    href: '/admin/categories',
    stats: { total: 0, active: 0, lastUpdated: 'Just now' },
    status: 'online',
    glowColor: '#B91BFF'
  },
  {
    id: 'custom-fields',
    name: 'Custom Fields',
    description: 'Dynamic field configuration matrix',
    icon: Tag,
    href: '/admin/custom-fields',
    stats: { total: 12, active: 10, lastUpdated: '2h ago' },
    status: 'online',
    glowColor: '#00F0FF'
  },
  {
    id: 'locations',
    name: 'Locations',
    description: 'Warehouse grid management system',
    icon: MapPin,
    href: '/admin/locations',
    stats: { total: 8, active: 8, lastUpdated: '1h ago' },
    status: 'online',
    glowColor: '#FF6B35'
  },
  {
    id: 'totes',
    name: 'Totes',
    description: 'Container allocation protocols',
    icon: Package,
    href: '/admin/totes',
    stats: { total: 45, active: 42, lastUpdated: '30m ago' },
    status: 'online',
    glowColor: '#B91BFF'
  },
  {
    id: 'shelves',
    name: 'Shelves',
    description: 'Tiered storage management',
    icon: BookOpen,
    href: '/admin/shelves',
    stats: { total: 12, active: 12, lastUpdated: '15m ago' },
    status: 'online',
    glowColor: '#00F0FF'
  },
  {
    id: 'tiers',
    name: 'Tiers',
    description: 'Vertical shelf tier management',
    icon: GripVertical,
    href: '/admin/tiers',
    stats: { total: 0, active: 0, lastUpdated: 'Just now' },
    status: 'online',
    glowColor: '#00F0FF'
  },
  {
    id: 'returns',
    name: 'Returns',
    description: 'RMA and return processing',
    icon: RotateCcw,
    href: '/admin/returns',
    stats: { total: 0, active: 0, lastUpdated: 'Just now' },
    status: 'online',
    glowColor: '#FF006E'
  },
  {
    id: 'system-picks',
    name: 'System Picks',
    description: 'Enumeration value registry',
    icon: List,
    href: '/admin/system-picks',
    stats: { total: 25, active: 25, lastUpdated: '3h ago' },
    status: 'online',
    glowColor: '#6BCF7F'
  },
  {
    id: 'label-designer',
    name: 'Label Designer',
    description: 'Thermal label template studio',
    icon: LabelIcon,
    href: '/admin/label-designer',
    stats: { total: 1, active: 1, lastUpdated: '5m ago' },
    status: 'online',
    glowColor: '#00F0FF'
  },
  {
    id: 'template-mapping',
    name: 'Template Mapping',
    description: 'Print template neural links',
    icon: FileCode,
    href: '/admin/template-mapping',
    stats: { total: 6, active: 5, lastUpdated: '2h ago' },
    status: 'warning',
    glowColor: '#FFA500'
  }
]

export default function AdminPage() {
  useShell({ title: 'Admin Control' })

  // Load saved preferences from localStorage
  const loadPreferences = () => {
    const saved = localStorage.getItem('adminCardPreferences')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return {}
      }
    }
    return {}
  }

  const [cardPreferences, setCardPreferences] = useState<Record<string, { visible: boolean; order: number }>>(loadPreferences())
  const [hiddenCardsExpanded, setHiddenCardsExpanded] = useState(false)
  const [draggedCard, setDraggedCard] = useState<string | null>(null)
  const [systemOverviewExpanded, setSystemOverviewExpanded] = useState(false)

  // Save preferences to localStorage
  const savePreferences = (prefs: Record<string, { visible: boolean; order: number }>) => {
    localStorage.setItem('adminCardPreferences', JSON.stringify(prefs))
    setCardPreferences(prefs)
  }

  const toggleCardVisibility = (cardId: string) => {
    const currentOrder = cardPreferences[cardId]?.order ?? managementAreas.findIndex(a => a.id === cardId)
    const newPrefs = {
      ...cardPreferences,
      [cardId]: {
        visible: !(cardPreferences[cardId]?.visible ?? true),
        order: currentOrder
      }
    }
    savePreferences(newPrefs)
  }

  const handleDragStart = (cardId: string) => {
    setDraggedCard(cardId)
  }

  const handleDragOver = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault()
    if (!draggedCard || draggedCard === targetCardId) return

    const draggedOrder = cardPreferences[draggedCard]?.order ?? managementAreas.findIndex(a => a.id === draggedCard)
    const targetOrder = cardPreferences[targetCardId]?.order ?? managementAreas.findIndex(a => a.id === targetCardId)

    const newPrefs = { ...cardPreferences }

    // Swap orders
    newPrefs[draggedCard] = {
      visible: newPrefs[draggedCard]?.visible ?? true,
      order: targetOrder
    }
    newPrefs[targetCardId] = {
      visible: newPrefs[targetCardId]?.visible ?? true,
      order: draggedOrder
    }

    savePreferences(newPrefs)
  }

  const handleDragEnd = () => {
    setDraggedCard(null)
  }

  // Sort and filter cards
  const sortedAreas = [...managementAreas]
    .map(area => ({
      ...area,
      visible: cardPreferences[area.id]?.visible ?? true,
      order: cardPreferences[area.id]?.order ?? managementAreas.findIndex(a => a.id === area.id)
    }))
    .sort((a, b) => a.order - b.order)

  const visibleAreas = sortedAreas.filter(area => area.visible)
  const hiddenAreas = sortedAreas.filter(area => !area.visible)

  const [systemStats] = useState({
    totalItems: 1247,
    totalUsers: 15,
    activeUsers: 12,
    totalLocations: 8,
    totalTotes: 45,
    systemHealth: 98.7,
    lastBackup: '2h ago',
    uptime: '99.9%',
    cpuUsage: 23,
    memoryUsage: 45,
    storageUsed: 67
  })

  const getStatusIcon = (status: ManagementArea['status']) => {
    switch (status) {
      case 'online':
        return <CheckCircle style={{ width: 16, height: 16, color: '#00FF00' }} />
      case 'warning':
        return <AlertTriangle style={{ width: 16, height: 16, color: '#FFA500' }} />
      case 'critical':
        return <AlertTriangle style={{ width: 16, height: 16, color: '#FF0000' }} />
    }
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header with System Stats */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              BGS CORE // ADMIN
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              SYSTEM CONTROL INTERFACE v2.0
            </p>
          </div>
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{
              background: 'rgba(255, 107, 53, 0.1)',
              border: '2px solid #FF6B35',
              borderRadius: '8px',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Activity style={{ width: 24, height: 24, color: '#FF6B35' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#8A95AA', letterSpacing: '1px' }}>SYSTEM HEALTH</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#00FF00' }}>{systemStats.systemHealth}%</div>
              </div>
            </div>
            <div style={{
              background: 'rgba(0, 240, 255, 0.1)',
              border: '2px solid #00F0FF',
              borderRadius: '8px',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Clock style={{ width: 24, height: 24, color: '#00F0FF' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#8A95AA', letterSpacing: '1px' }}>UPTIME</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#E0E6F0' }}>{systemStats.uptime}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginTop: '20px'
        }}>
          {[
            { label: 'INVENTORY ITEMS', value: systemStats.totalItems, color: '#FF6B35' },
            { label: 'ACTIVE USERS', value: `${systemStats.activeUsers}/${systemStats.totalUsers}`, color: '#00F0FF' },
            { label: 'LOCATIONS', value: systemStats.totalLocations, color: '#B91BFF' },
            { label: 'TOTES', value: systemStats.totalTotes, color: '#FFD93D' },
            { label: 'CPU', value: `${systemStats.cpuUsage}%`, color: '#6BCF7F' },
            { label: 'MEMORY', value: `${systemStats.memoryUsage}%`, color: '#FF1493' },
            { label: 'STORAGE', value: `${systemStats.storageUsed}%`, color: '#FFA500' }
          ].map((stat, idx) => (
            <div key={idx} style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.05), rgba(255, 107, 53, 0.02))',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '6px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                {stat.label}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: stat.color }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Overview Section */}
      <div style={{
        marginBottom: '32px',
        border: '2px solid #9D4EDD',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <button
          onClick={() => setSystemOverviewExpanded(!systemOverviewExpanded)}
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.1), rgba(157, 78, 221, 0.05))',
            border: 'none',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            fontFamily: 'Rajdhani, sans-serif'
          }}
        >
          <span style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#9D4EDD',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '0 0 20px rgba(157, 78, 221, 0.4)'
          }}>
            System Overview
          </span>
          {systemOverviewExpanded ? (
            <ChevronUp style={{ width: 24, height: 24, color: '#9D4EDD' }} />
          ) : (
            <ChevronDown style={{ width: 24, height: 24, color: '#9D4EDD' }} />
          )}
        </button>

        {systemOverviewExpanded && (
          <div style={{
            padding: '20px',
            background: 'rgba(26, 31, 46, 0.5)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              <ICCounterCard />
              <SystemHealthCard />
              <AICoreCard />
              <UsersCard />
              <AIPromptsCard />
            </div>
          </div>
        )}
      </div>

      {/* Management Areas Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {visibleAreas.map((area) => (
          <div
            key={area.id}
            draggable
            onDragStart={() => handleDragStart(area.id)}
            onDragOver={(e) => handleDragOver(e, area.id)}
            onDragEnd={handleDragEnd}
            style={{
              position: 'relative'
            }}
          >
            {/* Hide/Reorder Controls */}
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              zIndex: 10,
              display: 'flex',
              gap: '4px'
            }}>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                style={{
                  background: 'rgba(26, 31, 46, 0.95)',
                  border: `1px solid ${area.glowColor}`,
                  borderRadius: '4px',
                  padding: '6px',
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: area.glowColor
                }}
                title="Drag to reorder"
              >
                <GripVertical style={{ width: 14, height: 14 }} />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleCardVisibility(area.id)
                }}
                style={{
                  background: 'rgba(26, 31, 46, 0.95)',
                  border: `1px solid ${area.glowColor}`,
                  borderRadius: '4px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: area.glowColor
                }}
                title="Hide card"
              >
                <EyeOff style={{ width: 14, height: 14 }} />
              </button>
            </div>

            <Link
              to={area.href}
              style={{
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <div style={{
              background: 'linear-gradient(135deg, #1A1F2E, #242938)',
              border: `2px solid ${area.glowColor}`,
              borderRadius: '8px',
              padding: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: `0 0 20px rgba(${area.glowColor === '#FF6B35' ? '255, 107, 53' : '0, 240, 255'}, 0.2)`,
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = `0 0 30px ${area.glowColor}80`
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = `0 0 20px ${area.glowColor}40`
            }}
            >
              {/* Animated Corner Accent */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `linear-gradient(135deg, transparent 50%, ${area.glowColor}20 50%)`,
                pointerEvents: 'none'
              }} />

              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div style={{
                  background: `${area.glowColor}20`,
                  border: `2px solid ${area.glowColor}`,
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <area.icon style={{ width: 28, height: 28, color: area.glowColor }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(area.status)}
                  <span style={{
                    fontSize: '12px',
                    color: area.status === 'online' ? '#00FF00' : area.status === 'warning' ? '#FFA500' : '#FF0000',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontWeight: 700
                  }}>
                    {area.status}
                  </span>
                </div>
              </div>

              {/* Title & Description */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: area.glowColor,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  margin: '0 0 8px 0',
                  textShadow: `0 0 10px ${area.glowColor}60`
                }}>
                  {area.name}
                </h3>
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  color: '#8A95AA',
                  margin: 0,
                  letterSpacing: '0.5px'
                }}>
                  {area.description}
                </p>
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: '20px',
                marginBottom: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(138, 149, 170, 0.2)'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                    TOTAL
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                    {area.stats.total}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                    ACTIVE
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#00FF00' }}>
                    {area.stats.active}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                    UPDATED
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#E0E6F0' }}>
                    {area.stats.lastUpdated}
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '8px',
                color: area.glowColor,
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                ACCESS MODULE
                <ChevronRight style={{ width: 18, height: 18 }} />
              </div>
            </div>
          </Link>
        </div>
        ))}
      </div>

      {/* Hidden Cards Section */}
      {hiddenAreas.length > 0 && (
        <div style={{
          marginBottom: '32px',
          border: '2px solid #8A95AA',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <button
            onClick={() => setHiddenCardsExpanded(!hiddenCardsExpanded)}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, rgba(138, 149, 170, 0.1), rgba(138, 149, 170, 0.05))',
              border: 'none',
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#E0E6F0',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <span>Hidden Cards ({hiddenAreas.length})</span>
            {hiddenCardsExpanded ? (
              <ChevronUp style={{ width: 20, height: 20 }} />
            ) : (
              <ChevronDown style={{ width: 20, height: 20 }} />
            )}
          </button>

          {hiddenCardsExpanded && (
            <div style={{
              padding: '20px',
              background: 'rgba(26, 31, 46, 0.5)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {hiddenAreas.map((area) => (
                <div
                  key={area.id}
                  style={{
                    background: 'linear-gradient(135deg, #1A1F2E, #242938)',
                    border: `1px solid ${area.glowColor}`,
                    borderRadius: '6px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <area.icon style={{ width: 20, height: 20, color: area.glowColor }} />
                    <span style={{
                      color: '#E0E6F0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600
                    }}>
                      {area.name}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleCardVisibility(area.id)}
                    style={{
                      background: `${area.glowColor}20`,
                      border: `1px solid ${area.glowColor}`,
                      borderRadius: '4px',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      color: area.glowColor,
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Show
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom Status Bar */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 107, 53, 0.05))',
        border: '2px solid #FF6B35',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#00FF00',
              boxShadow: '0 0 10px #00FF00',
              animation: 'pulse 2s infinite'
            }} />
            <span style={{ color: '#E0E6F0', fontSize: '14px', letterSpacing: '1px' }}>
              ALL SYSTEMS OPERATIONAL
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#8A95AA', letterSpacing: '1px' }}>
            Last Backup: {systemStats.lastBackup}
          </div>
        </div>
        <button style={{
          background: 'linear-gradient(135deg, #FF6B35, #F97316)',
          border: '2px solid #FF6B35',
          borderRadius: '6px',
          padding: '10px 20px',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 107, 53, 0.6)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.4)'
        }}
        >
          <RefreshCw style={{ width: 16, height: 16 }} />
          REFRESH STATUS
        </button>
      </div>

      {/* Add pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
